import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import api, { apiError } from "@/lib/api";
import { Logo } from "@/components/Logo";

import { toast } from "sonner";
import { Loader2, HeartHandshake } from "lucide-react";

export default function Register() {
    const [form, setForm] = useState({ name: "", phone: "", password: "", email: "", referral_code: "", guardian_code: "" });
    const [busy, setBusy] = useState(false);
    const [consent, setConsent] = useState(false);
    const [isGuardian, setIsGuardian] = useState(false);
    const [society, setSociety] = useState(null);
    const { register } = useAuth();
    const { t } = useLang();
    const nav = useNavigate();

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const ref = params.get("ref");
        if (ref) setForm((f) => ({ ...f, referral_code: ref.toUpperCase() }));
        const gc = params.get("guardian") || params.get("gc");
        if (gc) { setIsGuardian(true); setForm((f) => ({ ...f, guardian_code: gc.toUpperCase() })); }
        const sc = params.get("society");
        if (sc) { api.get(`/public/society/${sc}`).then((r) => setSociety(r.data)).catch(() => {}); }
    }, []);

    const submit = async (e) => {
        e.preventDefault();
        if (!consent) return toast.error("Please accept the Terms & Privacy Policy");
        if (isGuardian && !form.guardian_code.trim()) return toast.error("Enter the family invite code");
        setBusy(true);
        try {
            const u = await register({
                name: form.name.trim(), phone: form.phone.trim(), password: form.password,
                email: form.email.trim() || null, referral_code: form.referral_code.trim() || null,
                guardian_code: isGuardian ? form.guardian_code.trim().toUpperCase() : null,
                society_code: society?.code || null,
            });
            api.post("/legal/accept", { context: "signup" }).catch(() => {});
            if (u?.guardian_of) { toast.success("Linked! You now help manage their Care."); nav("/care"); }
            else if (society) { sessionStorage.setItem("homiie_just_joined", society.code); toast.success(`Welcome to HOMIIE, ${society.name} resident!`); nav(`/society/${society.code}`); }
            else { toast.success("Account created! Welcome to HOMIIE."); nav("/"); }
        } catch (err) {
            toast.error(apiError(err.response?.data?.detail) || "Registration failed");
        } finally {
            setBusy(false);
        }
    };

    const fields = [
        { k: "name", label: "Full name", ph: "Rajesh Sharma", type: "text", req: true },
        { k: "phone", label: "Mobile number", ph: "98xxxxxxxx", type: "tel", req: true },
        { k: "password", label: "Password", ph: "Create a password", type: "password", req: true },
        { k: "email", label: "Email (optional)", ph: "you@example.com", type: "email", req: false },
        { k: "referral_code", label: "Referral / Pro code (optional)", ph: "RAMESH1047", type: "text", req: false },
    ];

    return (
        <div className="mx-auto flex min-h-screen max-w-md flex-col justify-center bg-lightbg px-6 py-10">
            <div className="mb-6">
                <Logo size={46} />
                <h1 className="mt-6 font-heading text-3xl font-extrabold text-navy">{t("Create your account")}</h1>
                <p className="mt-1 text-sm text-navy/55">{t("Join HOMIIE and manage your home with ease.")}</p>
                {society && <p data-testid="register-society-banner" className="mt-3 rounded-xl border border-teal/20 bg-white px-3 py-2 text-sm font-semibold text-navy">Joining as a resident of <b>{society.name}</b></p>}
            </div>
            <form onSubmit={submit} className="space-y-3.5">
                {fields.map((f) => (
                    <div key={f.k}>
                        <label className="mb-1.5 block text-sm font-semibold text-navy">{t(f.label)}</label>
                        <input
                            data-testid={`register-${f.k}-input`}
                            type={f.type}
                            value={form[f.k]}
                            onChange={set(f.k)}
                            placeholder={f.ph}
                            required={f.req}
                            className="w-full rounded-xl border border-navy/15 bg-white px-4 py-3 text-navy outline-none transition-colors focus:border-orange"
                        />
                    </div>
                ))}
                <div className="rounded-xl border border-navy/15 bg-white p-3.5">
                    <label className="flex cursor-pointer items-center justify-between" data-testid="guardian-toggle-label">
                        <span className="flex items-center gap-2 text-sm font-semibold text-navy">
                            <HeartHandshake size={17} className="text-[#F43F5E]" /> I'm a family guardian
                        </span>
                        <input data-testid="guardian-toggle" type="checkbox" checked={isGuardian} onChange={(e) => setIsGuardian(e.target.checked)} className="h-5 w-5 accent-[#F43F5E]" />
                    </label>
                    {isGuardian && (
                        <div className="mt-3">
                            <label className="mb-1.5 block text-xs font-semibold text-navy/60">Family invite code</label>
                            <input data-testid="register-guardian-code-input" value={form.guardian_code} onChange={(e) => setForm({ ...form, guardian_code: e.target.value.toUpperCase() })} placeholder="e.g. 7FQ2KD" maxLength={6}
                                className="w-full rounded-xl border border-navy/15 px-4 py-3 text-center font-heading text-lg font-extrabold tracking-[0.25em] text-navy outline-none focus:border-[#F43F5E]" />
                            <p className="mt-1.5 text-[11px] leading-relaxed text-navy/50">Ask your family member to open HOMIIE Care → Family &amp; Guardians and share their code.</p>
                        </div>
                    )}
                </div>
                <label data-testid="register-consent-label" className="flex cursor-pointer items-start gap-3 rounded-xl border border-navy/15 bg-white p-3.5">
                    <input data-testid="register-consent" type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5 h-5 w-5 shrink-0 accent-orange" />
                    <span className="text-xs leading-relaxed text-navy/70">
                        I agree to HOMIIE{" "}
                        <Link to="/legal/terms-and-conditions" target="_blank" className="font-bold text-orange">Terms &amp; Conditions</Link> and{" "}
                        <Link to="/legal/privacy-policy" target="_blank" className="font-bold text-orange">Privacy Policy</Link>.
                    </span>
                </label>
                <button
                    data-testid="register-submit-btn"
                    disabled={busy || !consent}
                    className="flex w-full items-center justify-center gap-2 rounded-full bg-orange py-3.5 text-sm font-bold text-white shadow-lg shadow-orange/30 transition-transform active:scale-95 disabled:opacity-40"
                >
                    {busy && <Loader2 className="h-4 w-4 animate-spin" />} {t("CREATE ACCOUNT")}
                </button>
            </form>

            <p className="mt-5 text-center text-sm text-navy/60">
                {t("Already have an account?")}{" "}
                <Link to={society ? `/society/${society.code}` : "/login"} data-testid="go-login-link" className="font-bold text-orange">{t("Log in")}</Link>
            </p>
        </div>
    );
}
